<template>
  <div
    class="project-ci-cd-settings-table__item"
    :class="{ 'has-errors': errors }"
  >
    <div class="project-ci-cd-settings-table__item__cell__common__input-group">
      <input
        class="form-control form-control-sm"
        :value="ciCdVariable?.key"
        :placeholder="variableInputIdPlaceholder"
        :name="variableInputIdName"
        :class="{ 'border-danger': errors }"
        :spellcheck="false"
        ref="inputVariableId"
        @input="emitId"
      />
      <div
        class="project-ci-cd-settings-table__item__cell__common__input-group__item"
        :class="{ 'border-danger': errors }"
      >
        <gf-custom-clipboard
          show-only-button="true"
          :text-to-copy="ciCdVariable?.key"
          :show-tooltip="false"
        />
      </div>
    </div>
    <div
      class="project-ci-cd-settings-table__item__cell__common__input-group"
      :class="{ '--hidden': isHidden && inputVariableValue?.length > 0 }"
      v-click-outside="hideValue"
    >
      <textarea
        class="form-control form-control-sm"
        :value="ciCdVariable?.value"
        :placeholder="variableInputValuePlaceholder"
        :name="variableInputValueName"
        ref="inputVariableValue"
        :rows="isHidden ? 1 : 3"
        :spellcheck="false"
        @click="isHidden = false"
        @input="emitValue"
      />
      <div class="project-ci-cd-settings-table__item__cell__common__input-group__item">
        <button
          v-if="inputVariableValue?.length > 0"
          class="btn btn-sm text-muted disable-hover"
          type="button"
          @click="toggleHideValue"
        >
          <i :class="ciCdValueHideValueIcon"></i>
        </button>
      </div>
      <div class="project-ci-cd-settings-table__item__cell__common__input-group__item">
        <gf-custom-clipboard
          show-only-button="true"
          :text-to-copy="ciCdVariable?.value"
          :show-tooltip="false"
        />
      </div>
    </div>
    <div class="project-ci-cd-settings-table__item__cell__common__input-group">
      <input
        class="form-control form-control-sm"
        :value="ciCdVariable?.environmentScope"
        :placeholder="variableInputEnvironmentPlaceholder"
        :name="variableInputEnvironmentName"
        :spellcheck="false"
        ref="inputVariableEnvironment"
        @input="emitEnvironment"
      />
      <div class="project-ci-cd-settings-table__item__cell__common__input-group__item">
        <gf-custom-clipboard
          show-only-button="true"
          :text-to-copy="ciCdVariable?.environmentScope || ''"
          :show-tooltip="false"
        />
      </div>
    </div>
    <div class="project-ci-cd-settings-table__item__cell__common">
      <div class="custom-control custom-switch">
        <input
          v-model="ciCdVariable.mask"
          class="custom-control-input"
          type="checkbox"
          :id="`maskToggler${ciCdVariable.key}`"
          @click="emitMaskValue"
        />
        <label
          class="custom-control-label"
          :for="`maskToggler${ciCdVariable.key}`"
        ></label>
      </div>
    </div>
    <div class="project-ci-cd-settings-table__item__cell__delete">
      <button
        v-if="ciCdVariable.canBeDeleted || ciCdVariable.uuid"
        type="button"
        class="d-flex align-items-center btn btn-sm btn-danger"
        @click="emitDelete"
      >
        <i class="gf-icon gf-icon-trash"></i>
      </button>
    </div>
    <div class="project-ci-cd-settings-table__item__cell__errors">
      <gf-custom-alert
        v-if="errors"
        variant="danger"
      >
        <template #body>
          <div
            class="text-truncate"
            v-for="(error, index) in formatedErrors"
            :key="index"
          >
            {{ error }}
          </div>
        </template>
      </gf-custom-alert>
    </div>
  </div>
</template>

<script>
import GfCustomClipboard from '@/shared-modules/shared-components/shared/GfCustomClipboard';
import ProjectSettingsMessages from '@shared/modules/project-settings/project.settings.messages';
import { clickOutsideDirective } from '@/directives.js';
import GfCustomAlert from '@/shared-modules/shared-components/shared/GfCustomAlert.vue';

export default {
  name: 'ProjectCiCdSettingsFormTableItem',
  components: { GfCustomClipboard, GfCustomAlert },
  directives: {
    'click-outside': clickOutsideDirective()
  },
  props: {
    // CI-CD Variable
    ciCdVariable: {
      type: Object,
      default: null
    },
    // ID input props
    variableInputIdName: {
      type: String,
      default: null
    },
    variableInputIdPlaceholder: {
      type: String,
      default() {
        return this.$t('ci_cd.placeholders.key');
      }
    },
    variableInputIdInitialValue: {
      type: String,
      default: null
    },
    variableInputIdDisabled: {
      type: Boolean,
      default: true
    },
    // Value input props
    variableInputValueInitialValue: {
      type: String,
      default: null
    },
    variableInputValueName: {
      type: String,
      default: null
    },
    variableInputValuePlaceholder: {
      type: String,
      default() {
        return this.$t('ci_cd.placeholders.value');
      }
    },
    // environment input props
    variableInputEnvironmentInitialValue: {
      type: String,
      default: null
    },
    variableInputEnvironmentName: {
      type: String,
      default: null
    },
    variableInputEnvironmentPlaceholder: {
      type: String,
      default() {
        return this.$t('ci_cd.placeholders.environment');
      }
    },
    errors: {
      default: null
    }
  },
  data() {
    return {
      inputVariableId: null,
      inputVariableValue: null,
      inputVariableEnvironment: null,
      isHidden: true
    };
  },
  computed: {
    ciCdValueHideValueIcon() {
      return this.isHidden ? 'gf-icon gf-icon-eye-lock' : 'gf-icon gf-icon-eye';
    },
    formatedErrors() {
      return typeof this.errors === 'object' ? Object.values(this.errors) : [this.errors];
    }
  },
  mounted() {
    this.getExistingValues();
    if (this.ciCdVariable) {
      this.inputVariableValue = this.ciCdVariable?.key;
      this.inputVariableEnvironment = this.ciCdVariable?.environmentScope;
    }
  },
  methods: {
    emitDelete() {
      this.$emit('delete');
    },
    emitId() {
      if (this.variableInputIdDisabled) {
        this.emitValue();
      } else {
        this.inputVariableId = this.$refs.inputVariableId?.value;
        this.$emit('inputId', this.inputVariableId);
      }
    },
    emitValue() {
      this.inputVariableValue = this.$refs.inputVariableValue?.value;
      this.inputVariableId = this.$refs.inputVariableId?.value;
      this.inputVariableEnvironment =
        this.$refs.inputVariableEnvironment?.value?.length > 0
          ? this.$refs.inputVariableEnvironment?.value
          : null;
      this.$emit('inputValue', {
        value: this.inputVariableValue,
        key: this.inputVariableId,
        environmentScope: this.inputVariableEnvironment
      });
    },
    emitEnvironment() {
      if (this.variableInputIdDisabled) {
        this.emitValue();
      } else {
        this.inputVariableEnvironment = this.$refs.inputVariableEnvironment?.value;
        this.$emit('inputEnvironment', this.inputVariableEnvironment);
      }
    },
    emitMaskValue() {
      this.$emit('maskToggle', this.ciCdVariable.mask);
    },
    toggleHideValue() {
      this.isHidden = !this.isHidden;
    },
    getExistingValues() {
      this.inputVariableId = this.variableInputIdInitialValue;
      this.inputVariableValue = this.variableInputValueInitialValue;
      this.inputVariableEnvironment = this.variableInputEnvironmentInitialValue;
    },
    hideValue() {
      this.isHidden = true;
      this.emitValue();
    }
  },
  i18n: {
    messages: ProjectSettingsMessages
  }
};
</script>
